/**
 * Phase 2C.3 — Assessment Preview (4 personas).
 *
 *   <PreviewDrawer/>          — top-level Sheet (or full-screen overlay)
 *      ↳ Persona switcher: Candidate · Reviewer · Mobile · Print/PDF
 *      ↳ Settings gear  : show_answers · show_competencies · show_weights · language · font_size
 *      ↳ Full-screen toggle (opens a window-level overlay)
 *      ↳ Per-persona toggles:
 *          • Mobile  → portrait / landscape
 *          • Print   → A4 / Letter + page boundaries + Download PDF
 *      ↳ Candidate summary panel (asset name · est time · sections · items)
 *      ↳ Reuses shared <AssessmentRenderer> for fidelity with EndUserAssessment.
 *
 * Architecture choice: rendering is shared across personas. The
 * Renderer applies `mode` + `settings` to choose what to display
 * (reveal correct answers, show metadata badges, etc.). This means
 * the Candidate persona uses the SAME RadioGroup / Textarea / Card
 * shapes as the commercial end-user flow.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuCheckboxItem, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Eye, Smartphone, Tablet, Printer, Settings, Maximize2, Minimize2,
  Download, RotateCw, FileText, Loader2,
} from 'lucide-react';

import i18next from 'i18next';

const PERSONAS = [
  { code: 'candidate', i18n: 'studio.preview.candidate', Icon: Eye },
  { code: 'reviewer',  i18n: 'studio.preview.reviewer',  Icon: FileText },
  { code: 'mobile',    i18n: 'studio.preview.mobile',    Icon: Smartphone },
  { code: 'print',     i18n: 'studio.preview.print',     Icon: Printer },
];

const DEFAULT_SETTINGS = {
  show_answers: false,
  show_competencies: false,
  show_weights: false,
  language: 'auto',     // 'auto' | 'en' | 'es'
  font_size: 'md',      // 'sm' | 'md' | 'lg'
};

/* ============================================================================
 *                              PREVIEW DRAWER (root)
 * ========================================================================== */
export function PreviewDrawer({ open, onClose, questionnaire, sections, questions, apiBase, authToken }) {
  const { t, i18n } = useTranslation();
  const [persona, setPersona] = useState('candidate');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [fullScreen, setFullScreen] = useState(false);
  const [mobileOrientation, setMobileOrientation] = useState('portrait');
  const [pageSize, setPageSize] = useState('A4');

  // When persona = 'reviewer', force metadata on (founder rule).
  const effectiveSettings = useMemo(() => {
    if (persona === 'reviewer') {
      return { ...settings, show_answers: true, show_competencies: true, show_weights: true };
    }
    return settings;
  }, [persona, settings]);

  const buildPdfParams = () => new URLSearchParams({
    page_size: pageSize,
    persona: persona === 'print' ? (settings.show_answers ? 'reviewer' : 'candidate') : persona,
    show_answers: String(effectiveSettings.show_answers),
    show_competencies: String(effectiveSettings.show_competencies),
    show_weights: String(effectiveSettings.show_weights),
    font_size: String(_fontPx(effectiveSettings.font_size) - 1),
    locale: (i18n.language || 'en').startsWith('es') ? 'es' : 'en',
  });

  const fetchPdfBlob = () =>
    fetch(`${apiBase}/api/admin/studio/assessments/${questionnaire.id}/preview.pdf?${buildPdfParams().toString()}`, {
      credentials: 'include',
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    }).then((r) => { if (!r.ok) throw new Error('PDF failed'); return r.blob(); });

  const downloadPdf = () => {
    fetchPdfBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${questionnaire.name || 'assessment'}_${pageSize}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 500);
      })
      .catch(() => {
        toast.error(t('studio.preview.pdfFailed', { defaultValue: 'Could not generate the PDF. Try again.' }));
      });
  };

  // UAT A6 — explicit print action: opens the generated PDF in a new tab so the
  // browser's native print flow is available. Gives feedback in every path.
  const printPdf = () => {
    toast.info(t('studio.preview.printOpening', { defaultValue: 'Preparing PDF for printing…' }));
    fetchPdfBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (!win) {
          toast.error(t('studio.preview.printBlocked', { defaultValue: 'The browser blocked the print tab. Allow pop-ups or use Download PDF.' }));
          return;
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => {
        toast.error(t('studio.preview.pdfFailed', { defaultValue: 'Could not generate the PDF. Try again.' }));
      });
  };

  // Content the drawer renders inside its scroll surface — abstracted
  // so the full-screen overlay can reuse it.
  const body = (
    <PreviewBody
      persona={persona}
      settings={effectiveSettings}
      mobileOrientation={mobileOrientation}
      pageSize={pageSize}
      questionnaire={questionnaire}
      sections={sections}
      questions={questions}
      isEs={(i18n.language || '').startsWith('es')}
    />
  );

  const header = (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card/60 shrink-0" data-testid="studio-preview-controls">
      <div className="flex gap-1" data-testid="studio-preview-personas">
        {PERSONAS.map(({ code, i18n: key, Icon }) => (
          <Button
            key={code}
            variant={persona === code ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPersona(code)}
            data-testid={`studio-preview-persona-${code}`}
            className="h-7 text-xs gap-1"
          >
            <Icon className="h-3 w-3" />
            {code === 'candidate' && questionnaire?.type === 'profile'
              ? t('studio.preview.collaborator', { defaultValue: 'Collaborator' })
              : t(key, { defaultValue: code })}
          </Button>
        ))}
      </div>
      <div className="flex-1" />
      {persona === 'mobile' && (
        <Button
          variant="outline" size="sm"
          onClick={() => setMobileOrientation((o) => (o === 'portrait' ? 'landscape' : 'portrait'))}
          data-testid="studio-preview-mobile-orient"
          className="h-7 text-xs gap-1"
          title="Toggle orientation"
        >
          <RotateCw className="h-3 w-3" />
          {mobileOrientation === 'portrait'
            ? t('studio.preview.portrait', { defaultValue: 'Portrait' })
            : t('studio.preview.landscape', { defaultValue: 'Landscape' })}
        </Button>
      )}
      {persona === 'print' && (
        <>
          <div className="inline-flex rounded-md border border-border overflow-hidden" role="tablist" data-testid="studio-preview-pagesize">
            {['A4', 'Letter'].map((sz) => (
              <button
                key={sz}
                type="button"
                onClick={() => setPageSize(sz)}
                data-testid={`studio-preview-pagesize-${sz.toLowerCase()}`}
                className={`h-7 px-2.5 text-xs transition-colors ${pageSize === sz ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted/60'}`}
              >
                {sz}
              </button>
            ))}
          </div>
          <Button
            variant="outline" size="sm"
            onClick={printPdf}
            data-testid="studio-preview-print-pdf"
            className="h-7 text-xs gap-1"
          >
            <Printer className="h-3 w-3" />
            {t('studio.preview.printNow', { defaultValue: 'Print' })}
          </Button>
          <Button
            variant="default" size="sm"
            onClick={downloadPdf}
            data-testid="studio-preview-download-pdf"
            className="h-7 text-xs gap-1"
          >
            <Download className="h-3 w-3" />
            {t('studio.preview.downloadPdf', { defaultValue: 'Download PDF' })}
          </Button>
        </>
      )}
      <SettingsMenu settings={settings} setSettings={setSettings} persona={persona} />
      <Button
        variant="outline" size="sm"
        onClick={() => setFullScreen((v) => !v)}
        data-testid="studio-preview-fullscreen"
        className="h-7 text-xs gap-1"
        title={fullScreen ? 'Exit full screen' : 'Open full screen'}
      >
        {fullScreen ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
      </Button>
    </div>
  );

  // Full-screen overlay — renders inside its own portal layer.
  if (open && fullScreen) {
    return (
      <div
        className="fixed inset-0 z-[60] bg-background flex flex-col"
        data-testid="studio-preview-fullscreen-overlay"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
          <p className="font-display font-semibold text-sm">
            {t('studio.preview.title', { defaultValue: 'Preview' })} · {questionnaire.name}
          </p>
          <Button
            variant="ghost" size="sm"
            onClick={() => { setFullScreen(false); onClose(); }}
            data-testid="studio-preview-close"
          >
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        </div>
        {header}
        <div className="flex-1 overflow-y-auto">{body}</div>
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl flex flex-col p-0"
        data-testid="studio-preview-drawer"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <SheetTitle className="text-base inline-flex items-center gap-2">
            <Eye className="h-4 w-4" />
            {t('studio.preview.title', { defaultValue: 'Preview' })}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {t('studio.preview.subtitle', {
              defaultValue: 'See how the assessment looks to candidates, reviewers, on mobile, or in print.',
            })}
          </SheetDescription>
        </SheetHeader>
        {header}
        <div className="flex-1 overflow-y-auto">{body}</div>
      </SheetContent>
    </Sheet>
  );
}

/* ============================================================================
 *                            SETTINGS DROPDOWN
 * ========================================================================== */
function SettingsMenu({ settings, setSettings, persona }) {
  const { t } = useTranslation();
  const isReviewer = persona === 'reviewer';
  const toggle = (key) => setSettings((s) => ({ ...s, [key]: !s[key] }));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline" size="sm"
          data-testid="studio-preview-settings-button"
          className="h-7 text-xs gap-1"
          title={t('studio.preview.settings', { defaultValue: 'Settings' })}
        >
          <Settings className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="studio-preview-settings-menu">
        <DropdownMenuLabel className="text-xs">
          {t('studio.preview.settings', { defaultValue: 'Preview settings' })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={settings.show_answers || isReviewer}
          disabled={isReviewer}
          onCheckedChange={() => toggle('show_answers')}
          data-testid="studio-preview-setting-answers"
        >
          {t('studio.preview.showAnswers', { defaultValue: 'Show correct answers' })}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={settings.show_competencies || isReviewer}
          disabled={isReviewer}
          onCheckedChange={() => toggle('show_competencies')}
          data-testid="studio-preview-setting-competencies"
        >
          {t('studio.preview.showCompetencies', { defaultValue: 'Show competencies' })}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={settings.show_weights || isReviewer}
          disabled={isReviewer}
          onCheckedChange={() => toggle('show_weights')}
          data-testid="studio-preview-setting-weights"
        >
          {t('studio.preview.showWeights', { defaultValue: 'Show weights' })}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('studio.preview.language', { defaultValue: 'Language' })}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={settings.language}
          onValueChange={(v) => setSettings((s) => ({ ...s, language: v }))}
        >
          {['auto', 'en', 'es'].map((lng) => (
            <DropdownMenuRadioItem
              key={lng} value={lng}
              data-testid={`studio-preview-language-${lng}`}
            >
              {lng === 'auto' ? t('studio.preview.languageAuto', { defaultValue: 'Auto' }) : lng.toUpperCase()}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('studio.preview.fontSize', { defaultValue: 'Font size' })}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={settings.font_size}
          onValueChange={(v) => setSettings((s) => ({ ...s, font_size: v }))}
        >
          {['sm', 'md', 'lg'].map((sz) => (
            <DropdownMenuRadioItem
              key={sz} value={sz}
              data-testid={`studio-preview-fontsize-${sz}`}
            >
              {sz.toUpperCase()}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Localized competency display via the existing reports.competencies key family;
// falls back to a humanized version of the technical code.
function txCompetency(code, isEs) {
  const key = `reports.competencies.${code}`;
  const val = i18next.t(key, { lng: isEs ? 'es' : 'en', defaultValue: '' });
  if (val) return val;
  return String(code).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// A10 — same rule-of-thumb the editor footer uses (kept in sync manually).
function _heuristicMinutes(questions) {
  const perType = { single_choice: 0.5, multi_choice: 0.75, likert_5: 0.5, scale_1_10: 0.5, text: 1.5, long_text: 3, date: 0.5 };
  return Math.max(1, Math.round((questions || []).reduce((a, q) => a + (perType[q.type] ?? 1), 0)));
}

function _fontPx(size) {
  return { sm: 12, md: 14, lg: 16 }[size] || 14;
}

/* ============================================================================
 *                            PREVIEW BODY
 * ========================================================================== */
function PreviewBody({ persona, settings, mobileOrientation, pageSize, questionnaire, sections, questions, isEs }) {
  if (persona === 'mobile') {
    return (
      <div className="p-6 flex justify-center bg-muted/30 min-h-full">
        <MobileDeviceFrame orientation={mobileOrientation}>
          <AssessmentRenderer
            persona="candidate"
            settings={settings}
            questionnaire={questionnaire}
            sections={sections}
            questions={questions}
            isEs={isEs}
            compact
          />
        </MobileDeviceFrame>
      </div>
    );
  }
  if (persona === 'print') {
    return (
      <PrintPreview
        pageSize={pageSize}
        settings={settings}
        questionnaire={questionnaire}
        sections={sections}
        questions={questions}
        isEs={isEs}
      />
    );
  }
  return (
    <div className="p-6">
      <AssessmentRenderer
        persona={persona}
        settings={settings}
        questionnaire={questionnaire}
        sections={sections}
        questions={questions}
        isEs={isEs}
      />
    </div>
  );
}

/* ============================================================================
 *                          ASSESSMENT RENDERER (shared)
 * ========================================================================== */
function AssessmentRenderer({ persona, settings, questionnaire, sections, questions, isEs, compact }) {
  const fontPx = _fontPx(settings.font_size);
  const reviewer = persona === 'reviewer';
  // Data convention: base fields are ES, `_en` variants are EN
  // (same as EndUserAssessment). Previously inverted → mixed-language preview.
  const tx = (es, en) => (isEs ? (es || en) : (en || es));
  const sectionMap = useMemo(() => {
    const map = {};
    sections.forEach((s) => { map[s.id] = s; });
    return map;
  }, [sections]);

  return (
    <div data-testid={`studio-preview-render-${persona}`} style={{ fontSize: fontPx }}>
      <CandidateSummary
        questionnaire={questionnaire}
        sections={sections}
        questions={questions}
        compact={compact}
      />
      {sections.map((sec, idx) => {
        const qs = questions.filter((q) => q.section_id === sec.id);
        return (
          <section key={sec.id} className="mb-8" data-testid={`studio-preview-section-${sec.id}`}>
            <header className="mb-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center">
                {isEs ? 'Sección' : 'Section'} {idx + 1}
                {reviewer && sec.weight !== undefined && (
                  <Badge variant="outline" className="ml-2 text-xs h-4 px-1">
                    {isEs ? 'peso' : 'weight'} {sec.weight}
                  </Badge>
                )}
              </div>
              <h2 className="font-display font-bold leading-tight" style={{ fontSize: fontPx + 6 }}>
                {((!isEs && sec.name_en) ? sec.name_en : sec.name) || (isEs ? 'Sin título' : 'Untitled')}
              </h2>
              {(sec.description || sec.description_en) && (
                <p className="text-muted-foreground mt-1" style={{ fontSize: fontPx - 1 }}>
                  {(!isEs && sec.description_en) ? sec.description_en : sec.description}
                </p>
              )}
            </header>
            <ol className="space-y-4">
              {qs.length === 0 && (
                <p className="text-muted-foreground italic" style={{ fontSize: fontPx - 2 }}>
                  {isEs ? 'No hay contenido en esta sección.' : 'No content in this section.'}
                </p>
              )}
              {qs.map((q, qIdx) => (
                <QuestionRow
                  key={q.id}
                  question={q}
                  section={sectionMap[q.section_id]}
                  index={qIdx + 1}
                  reviewer={reviewer}
                  settings={settings}
                  isEs={isEs}
                  fontPx={fontPx}
                  tx={tx}
                />
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

function CandidateSummary({ questionnaire, sections, questions, compact }) {
  const { t, i18n } = useTranslation();
  const isEs = (i18n.language || '').startsWith('es');
  const qName = (!isEs && questionnaire.name_en) ? questionnaire.name_en : questionnaire.name;
  const qObjective = (!isEs && questionnaire.objective_en) ? questionnaire.objective_en : questionnaire.objective;
  return (
    <Card className={`mb-6 ${compact ? 'p-3' : 'p-4'}`} data-testid="studio-preview-summary">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
        {t('studio.preview.summary', { defaultValue: 'About this assessment' })}
      </p>
      <h1 className="font-display font-bold tracking-tight" style={{ fontSize: compact ? 18 : 22 }}>
        {qName}
      </h1>
      {qObjective && (
        <p className="text-muted-foreground mt-1 text-sm">{qObjective}</p>
      )}
      <div className="grid grid-cols-3 gap-2 mt-3" data-testid="studio-preview-summary-stats">
        <SummaryStat
          label={t('studio.preview.estimatedTime', { defaultValue: 'Estimated time' })}
          value={questionnaire.estimated_minutes
            ? `${questionnaire.estimated_minutes} min`
            : `≈${_heuristicMinutes(questions)} min (${t('studio.preview.autoEstimate', { defaultValue: 'auto' })})`}
          testId="studio-preview-summary-time"
        />
        <SummaryStat
          label={t('studio.preview.sections', { defaultValue: 'Sections' })}
          value={sections.length}
          testId="studio-preview-summary-sections"
        />
        <SummaryStat
          label={t('studio.preview.items', { defaultValue: 'Items' })}
          value={questions.length}
          testId="studio-preview-summary-items"
        />
      </div>
    </Card>
  );
}

function SummaryStat({ label, value, testId }) {
  return (
    <div className="text-center" data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-display font-semibold text-base">{value}</p>
    </div>
  );
}

function QuestionRow({ question, section, index, reviewer, settings, isEs, fontPx, tx }) {
  const q = question;
  const showAnswers = settings.show_answers;
  const showCompetencies = settings.show_competencies;
  const showWeights = settings.show_weights;
  return (
    <li
      className="rounded-lg border border-border bg-card p-4"
      data-testid={`studio-preview-q-${q.id}`}
      data-stable-id={q.stable_id || ''}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0" style={{ fontSize: fontPx - 1 }}>
          {index}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug" style={{ fontSize: fontPx }}>
            {tx(q.prompt, q.prompt_en) || (isEs ? 'Sin texto' : 'Untitled')}
          </p>
        </div>
      </div>

      {q.type === 'date' ? (
        <div
          className="flex items-center gap-2 rounded-md border border-border px-3 py-2 w-56 text-muted-foreground text-sm"
          data-testid={`studio-preview-date-${q.id}`}
        >
          {isEs ? 'dd/mm/aaaa (selector de fecha)' : 'mm/dd/yyyy (date picker)'}
        </div>
      ) : q.type === 'text' || q.type === 'long_text' ? (
        <Textarea
          value=""
          readOnly
          rows={q.type === 'long_text' ? 5 : 2}
          placeholder={isEs ? 'Escribe tu respuesta aquí…' : 'Type your answer here…'}
          data-testid={`studio-preview-input-${q.id}`}
        />
      ) : q.type === 'scale_1_10' ? (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`studio-preview-scale-${q.id}`}>
          <span className="text-xs text-muted-foreground">{q.scale_low_label || (isEs ? 'Bajo' : 'Low')}</span>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <span key={n} className="h-7 w-7 rounded-md border border-border text-xs flex items-center justify-center font-semibold">{n}</span>
          ))}
          <span className="text-xs text-muted-foreground">{q.scale_high_label || (isEs ? 'Alto' : 'High')}</span>
        </div>
      ) : q.type === 'multi_choice' ? (
        <div className="space-y-2" data-testid={`studio-preview-multi-${q.id}`}>
          {(q.options || []).map((opt) => {
            const correct = showAnswers && opt.is_correct;
            return (
              <Label
                key={opt.id}
                className={`flex items-start gap-2 p-2 rounded-md border cursor-default ${correct ? 'border-success bg-success/10' : 'border-border'}`}
                data-testid={`studio-preview-option-${q.id}-${opt.id}`}
                data-correct={correct ? 'true' : 'false'}
              >
                <Checkbox checked={false} disabled />
                <span className="text-xs leading-snug font-medium">
                  {tx(opt.label, opt.label_en)}
                  {correct && <span className="ml-1 text-success">✓</span>}
                </span>
              </Label>
            );
          })}
        </div>
      ) : (
        // single_choice / likert_5 → RadioGroup
        <RadioGroup value="" className="space-y-2" data-testid={`studio-preview-radio-${q.id}`}>
          {(q.options || []).map((opt) => {
            const correct = showAnswers && opt.is_correct;
            return (
              <Label
                key={opt.id}
                className={`flex items-start gap-2 p-2 rounded-md border cursor-default ${correct ? 'border-success bg-success/10' : 'border-border'}`}
                data-testid={`studio-preview-option-${q.id}-${opt.id}`}
                data-correct={correct ? 'true' : 'false'}
              >
                <RadioGroupItem value={opt.id} disabled />
                <span className="text-xs leading-snug font-medium">
                  {tx(opt.label, opt.label_en)}
                  {correct && <span className="ml-1 text-success">✓</span>}
                </span>
              </Label>
            );
          })}
        </RadioGroup>
      )}

      {/* Reviewer metadata footer */}
      {reviewer && (
        <div className="mt-3 pt-2 border-t border-dashed border-border flex flex-wrap gap-1.5" data-testid={`studio-preview-meta-${q.id}`}>
          {q.stable_id && (
            <Badge variant="outline" className="text-xs font-mono h-4 px-1" data-testid="studio-preview-meta-stable-id">
              {q.stable_id}
            </Badge>
          )}
          {section?.name && (
            <Badge variant="outline" className="text-xs h-4 px-1" data-testid="studio-preview-meta-section">
              {isEs ? 'Sección' : 'Section'}: {section.name}
            </Badge>
          )}
          {q.difficulty !== null && q.difficulty !== undefined && (
            <Badge variant="outline" className="text-xs h-4 px-1" data-testid="studio-preview-meta-difficulty">
              D{q.difficulty}
            </Badge>
          )}
          {showWeights && q.weight !== undefined && (
            <Badge variant="outline" className="text-xs h-4 px-1" data-testid="studio-preview-meta-weight">
              {isEs ? 'Peso' : 'Weight'} {q.weight}
            </Badge>
          )}
          {showCompetencies && q.competency_map && Object.keys(q.competency_map).length > 0 && (
            <Badge variant="outline" className="text-xs h-4 px-1" data-testid="studio-preview-meta-competencies" title={Object.keys(q.competency_map).join(', ')}>
              {Object.keys(q.competency_map).slice(0, 3).map((code) => txCompetency(code, isEs)).join(', ')}
              {Object.keys(q.competency_map).length > 3 && '…'}
            </Badge>
          )}
        </div>
      )}
    </li>
  );
}

/* ============================================================================
 *                           MOBILE DEVICE FRAME
 * ========================================================================== */
function MobileDeviceFrame({ orientation, children }) {
  const portrait = orientation === 'portrait';
  const width = portrait ? 375 : 667;
  const height = portrait ? 667 : 375;
  return (
    <div
      className="bg-card border-[14px] border-gray-800 dark:border-gray-900 rounded-[36px] shadow-2xl overflow-hidden"
      style={{ width, height }}
      data-testid="studio-preview-mobile-frame"
      data-orientation={orientation}
    >
      <div className="h-full overflow-y-auto p-4">{children}</div>
    </div>
  );
}

/* ============================================================================
 *                           PRINT PREVIEW (page boundaries)
 * ========================================================================== */
function PrintPreview({ pageSize, settings, questionnaire, sections, questions, isEs }) {
  const { t } = useTranslation();
  const dims = pageSize === 'Letter'
    ? { w: 816, h: 1056 }   // 8.5x11" @96dpi
    : { w: 794, h: 1123 };  // A4 @96dpi
  // Approximate page-break heights — visual only.
  const pageInner = { w: dims.w - 96, h: dims.h - 144 };

  return (
    <div className="p-6 bg-muted/30 min-h-full" data-testid="studio-preview-print">
      <div className="mx-auto" style={{ maxWidth: dims.w + 32 }}>
        <p className="text-xs text-muted-foreground mb-2 text-center">
          {t('studio.preview.pageBoundariesHint', {
            defaultValue: 'Page boundaries shown approximate the {{size}} layout. Final pagination is computed by the PDF service.',
            size: pageSize,
          })}
        </p>
        {/* One simulated page (the PDF service handles real pagination). */}
        <Card
          className="bg-white text-black mx-auto shadow-lg"
          style={{ width: dims.w, minHeight: dims.h, padding: 48 }}
          data-testid="studio-preview-print-page"
          data-pagesize={pageSize}
        >
          <div style={{ width: pageInner.w }}>
            <AssessmentRenderer
              persona="reviewer"
              settings={settings}
              questionnaire={questionnaire}
              sections={sections}
              questions={questions}
              isEs={isEs}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

// Suppress unused-import lint warnings for `Tablet` and `Loader2` which are
// reserved for future Phase 2C.4 wiring.
void Tablet; void Loader2;
